/**
 * Post-promotion name-conflict dialog.
 *
 * A promotion that collides with an existing filename lands as a
 * deconflicted copy ("name (2).md"). This dialog lets the user turn that
 * outcome into an in-place replacement instead: it deletes the deconflicted
 * copy, then re-submits the promotion with `overwrite: true` so the original
 * file (and its indexed record) is updated in place — the flow for revising
 * plan templates without forking the filename.
 */
import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  useAddFromWorkspace,
  useDeleteFile,
} from "src/api/projectFolders/projectFolderService";
import type { AddFromWorkspaceRequest } from "src/api/projectFolders/types";

export interface PromoteConflict {
  folderId: number;
  /** Display name of the destination folder. */
  folderName: string;
  /** The name that already existed in the folder. */
  originalName: string;
  /** The "name (N)" copy the first promotion created. */
  deconflictedName: string;
  /** The original promotion request, re-sent with overwrite on replace. */
  request: AddFromWorkspaceRequest;
}

interface Props {
  conflict: PromoteConflict | null;
  /** Keep both files — dismiss without further writes. */
  onKeepBoth: () => void;
  /**
   * The original was replaced in place. `cleanupWarning` is set when the
   * deconflicted copy couldn't be removed afterwards — the replace itself
   * still succeeded, so callers should report success with the caveat.
   */
  onReplaced: (conflict: PromoteConflict, cleanupWarning?: string) => void;
  onError: (message: string) => void;
}

const ReplaceFileDialog: React.FC<Props> = ({
  conflict,
  onKeepBoth,
  onReplaced,
  onError,
}) => {
  const addFromWorkspace = useAddFromWorkspace();
  const deleteFile = useDeleteFile();
  const [busy, setBusy] = useState(false);

  const handleReplace = async () => {
    if (!conflict) return;
    setBusy(true);
    try {
      // Overwrite the original first, then remove the deconflicted copy.
      // This order degrades safely: an overwrite failure leaves both files
      // intact, and a cleanup failure leaves a harmless duplicate — whereas
      // deleting first would lose the copy if the overwrite then failed.
      await addFromWorkspace.mutateAsync({
        folderId: conflict.folderId,
        data: { ...conflict.request, overwrite: true },
      });
    } catch (err: unknown) {
      const detail = (err as any)?.response?.data?.detail;
      onError(
        typeof detail === "string"
          ? detail
          : "Failed to replace the existing file.",
      );
      setBusy(false);
      return;
    }
    try {
      await deleteFile.mutateAsync({
        folderId: conflict.folderId,
        fileName: conflict.deconflictedName,
      });
      onReplaced(conflict);
    } catch {
      // The replace already happened — only the duplicate cleanup failed.
      // Reporting this as a failed replace would be wrong (and onError
      // closes the dialog with no retry), so report success with a caveat.
      onReplaced(
        conflict,
        `the copy "${conflict.deconflictedName}" couldn't be removed`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!conflict} onClose={busy ? undefined : onKeepBoth}>
      <DialogTitle>File already exists</DialogTitle>
      <DialogContent>
        <DialogContentText>
          &ldquo;{conflict?.originalName}&rdquo; already exists in{" "}
          {conflict?.folderName}, so your file was added as &ldquo;
          {conflict?.deconflictedName}&rdquo;. Replace the existing file with
          this version instead?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onKeepBoth}
          disabled={busy}
          data-attr="projects:btn:conflict-keep-both"
        >
          Keep both
        </Button>
        <Button
          onClick={() => void handleReplace()}
          disabled={busy}
          variant="contained"
          data-attr="projects:btn:conflict-replace"
        >
          {busy ? "Replacing…" : "Replace existing"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReplaceFileDialog;
